import {
  LiquidGlassView as LiquidGlassViewFromLiquidGlass,
  isLiquidGlassSupported,
} from "@-/com.npmjs.callstack__liquid-glass";
import { cn } from "@-/com.npmjs.cn";
import { BlurView as BlurViewFromExpoBlur } from "@-/com.npmjs.expo-blur";
import { Platform } from "@-/com.npmjs.react-native";
import Animated, { createAnimatedComponent } from "@-/com.npmjs.react-native-reanimated";
import { useUniwind, withUniwind } from "@-/com.npmjs.uniwind";
import type { ComponentProps } from "react";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { NATIVE_MENU_CORNER_RADIUS } from "#native-menu-corner-radius";
import { useCappedCornerRadius } from "#use-capped-corner-radius";

const BlurView = withUniwind(BlurViewFromExpoBlur);
// Animated, so a caller can drive its corners (the toolbar popover's morph) as on the blur.
const AnimatedLiquidGlassView = createAnimatedComponent(
  withUniwind(LiquidGlassViewFromLiquidGlass),
);

/**
 * Shared material behind menu content; never intercepts menu gestures or affects measurement.
 *
 * On iOS Liquid Glass it is the system menu's material: an untinted regular glass, which UIKit
 * itself thickens (blur, bleed, refraction and shadow all scale with the glass's size). A presented
 * `UIMenu` uses that same effect, so the two render the same pixels over the same backdrop, with
 * the platter's 32pt corners. (Under about a 66pt short side UIKit makes any regular glass a
 * control's instead, tracking the backdrop's luminance; a one-row system menu keeps the platter's
 * look through API apps do not have, so only there do the two differ.)
 */
export function MenuPopoverSurface({
  className,
  material = "thick",
  style,
}: Pick<ComponentProps<typeof Animated.View>, "className" | "style"> & {
  /**
   * `thick` all but hides what's underneath; `regular` lets it show through as soft color while
   * still frosting labels past legibility — for a surface that lays glass over the blur. The
   * system menu glass on iOS Liquid Glass ignores it.
   */
  material?: "regular" | "thick";
}) {
  const { theme } = useUniwind();
  const dark = theme === "dark";
  const corners = useCappedCornerRadius(NATIVE_MENU_CORNER_RADIUS);
  if (isLiquidGlassSupported) {
    return (
      <AnimatedLiquidGlassView
        accessibilityElementsHidden
        accessible={false}
        // Not clipped by a wrapper: the glass draws its rim and shadow past its own bounds.
        className={cn("absolute inset-0", className)}
        // The menu's content takes the theme's inks, so its glass follows the theme too.
        colorScheme={dark ? "dark" : "light"}
        effect="regular"
        importantForAccessibility="no-hide-descendants"
        onLayout={corners.onLayout}
        pointerEvents="none"
        // The platter's corners, capped at a capsule (a glass takes no radius past it).
        style={[{ borderRadius: corners.borderRadius }, style]}
      />
    );
  }
  return (
    <Animated.View
      accessibilityElementsHidden
      accessible={false}
      className={cn("absolute inset-0 overflow-hidden rounded-xs", className)}
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={style}
    >
      {/* A frosted material obscures underlying labels instead of refracting them. */}
      {Platform.OS === "android" && (
        <FrostedSurface className="absolute inset-0" intensity={100} material={material} />
      )}
      {Platform.OS !== "android" && (
        <BlurView
          className="absolute inset-0"
          intensity={100}
          tint={
            Platform.OS === "ios"
              ? material === "thick"
                ? dark
                  ? "systemThickMaterialDark"
                  : "systemThickMaterialLight"
                : dark
                  ? "systemMaterialDark"
                  : "systemMaterialLight"
              : dark
                ? "dark"
                : "light"
          }
        />
      )}
    </Animated.View>
  );
}
